import React, {useEffect, useRef, useState} from "react";
import {Redirect, useParams} from "react-router-dom";
import UserService from "../../services/user.service";
import "../../Print.css";
import {logOut} from "../../helpers/logout";
import {Button, Divider, Grid, Toolbar} from "@material-ui/core";
import ActionButton from "../form/input/ActionButton";
import Tooltip from "@material-ui/core/Tooltip";
import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import {makeStyles} from "@material-ui/core/styles";
import clsx from "clsx";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import GeneratedDocumentEmailAction from "./GeneratedDocumentEmailAction";
import {PDFExport} from "@progress/kendo-react-pdf";
import {getPdfExportProxyProps, savePdfWithEmbeddedImages} from "../../helpers/pdfExport";
import {NotificationManager} from "react-notifications";

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
        width: "100%",
        backgroundColor: theme.palette.background.paper,
    },
    seeMore: {
        marginTop: theme.spacing(8),
    },
    paper: {
        padding: theme.spacing(2),
        display: "flex",
        overflow: "auto",
        flexDirection: "column",
    },
    toolbarButtons: {
        "& .MuiFormControl-root": {
            margin: theme.spacing(1),
        },
    },
    marginTop: {
        marginTop: theme.spacing(2),
    },
    editHint: {
        margin: "0 0 12px 0",
        fontWeight: "bold",
        textAlign: "center",
        color: "#2c4767",
    },
    document: {
        padding: "20mm 15mm 12mm 15mm",
        boxSizing: "border-box",
        background: "#ffffff",
        color: "#000000",
        fontFamily: '"DejaVu Sans", "Arial", sans-serif',
        fontSize: 12,
        lineHeight: 1.3,
    },
    copy: {
        minHeight: "123mm",
        paddingTop: "2mm",
    },
    secondCopy: {
        marginTop: "12mm",
    },
    header: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        position: "relative",
        marginBottom: "12mm",
    },
    logoPlaceholder: {
        position: "absolute",
        left: "7mm",
        top: "0",
        width: "18mm",
        height: "18mm",
        border: "1px solid #000",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 9,
        lineHeight: 1,
        textAlign: "center",
    },
    headerTitle: {
        textAlign: "center",
        fontWeight: "bold",
        lineHeight: 1.25,
    },
    title: {
        display: "inline-block",
        fontWeight: "bold",
        textDecoration: "underline",
        margin: "0 0 11mm 0",
    },
    formRow: {
        display: "flex",
        alignItems: "baseline",
        minHeight: "7mm",
    },
    label: {
        whiteSpace: "nowrap",
        marginRight: 4,
    },
    field: {
        flex: 1,
        border: 0,
        borderBottom: "1px dotted #000",
        background: "transparent",
        fontFamily: '"DejaVu Sans", "Arial", sans-serif',
        fontSize: 12,
        lineHeight: "16px",
        padding: "0 2px",
        minWidth: 0,
    },
    multilineField: {
        width: "100%",
        height: "12mm",
        border: 0,
        background: "repeating-linear-gradient(to bottom, transparent 0, transparent 17px, #000 18px, transparent 19px)",
        fontFamily: '"DejaVu Sans", "Arial", sans-serif',
        fontSize: 12,
        lineHeight: "18px",
        padding: "0 2px",
        boxSizing: "border-box",
        resize: "none",
        overflow: "hidden",
    },
    shortField: {
        flex: "0 0 52mm",
    },
    spacer: {
        height: "8mm",
    },
    smallSpacer: {
        height: "4mm",
    },
}));

const buildFuneralService = (user) => [
    user?.name,
    user?.address,
    user?.ico ? `IČO: ${user.ico}` : "",
    user?.phone,
].filter(Boolean).join(", ");

const buildOrderer = (funeral) => [
    funeral.contracting_authority,
    funeral.ca_address,
    funeral.ca_city,
    funeral.ca_phone,
].filter(Boolean).join(", ");

const CrematoriumEvidenceListGenerator = (props) => {
    const classes = useStyles();
    const fixedHeightPaper = clsx(classes.paper);
    const pdfRef = useRef(null);
    const funeralId = useParams();
    const userLocal = JSON.parse(localStorage.getItem("user")).id;

    const [funeral, setFuneral] = useState({
        _id: "",
        deceased: "",
        ca_email: "",
    });
    const [user, setUser] = useState(null);
    const [fields, setFields] = useState({
        funeralService: "",
        orderer: "",
        ceremony: "s obradom / bez obradu",
        invoiceNumber: "",
        urnSendTo: "",
    });
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        UserService.getUser(userLocal).then(
            (response) => {
                setUser(response.data);
                setFields((currentFields) => ({
                    ...currentFields,
                    funeralService: buildFuneralService(response.data),
                }));
            },
            (e) => {
                if (e.response.status === 401) {
                    logOut(props);
                    NotificationManager.warning("Potrebné sa znova prihlásiť!", "Upozornenie", 5000);
                }
            }
        );

        UserService.getFuneral(funeralId.funeral_id).then(
            (response) => {
                setFuneral(response.data);
                setFields((currentFields) => ({
                    ...currentFields,
                    orderer: buildOrderer(response.data),
                }));
            },
            (e) => {
                if (e.response.status === 401) {
                    logOut(props);
                    NotificationManager.warning("Potrebné sa znova prihlásiť!", "Upozornenie", 5000);
                }
            }
        );
    }, [funeralId.funeral_id, userLocal, props]);

    const handleChange = (event) => {
        const {name, value} = event.target;
        setFields({...fields, [name]: value});
    };

    const handleExport = () => {
        savePdfWithEmbeddedImages(pdfRef, (error) => {
            if (error) {
                NotificationManager.error("Nepodarilo sa exportovať PDF!", "Chyba", 5000);
            }
        });
    };

    const renderField = (name, className = "") => (
        <input
            name={name}
            onChange={handleChange}
            type="text"
            className={`${classes.field} ${className}`}
            value={fields[name]}
        />
    );

    const renderMultilineField = (name) => (
        <textarea
            name={name}
            onChange={handleChange}
            className={classes.multilineField}
            value={fields[name]}
        />
    );

    const renderCopy = (second = false) => (
        <div className={`${classes.copy} ${second ? classes.secondCopy : ""}`}>
            <div className={classes.header}>
                <div className={classes.logoPlaceholder}>SMSZ</div>
                <div className={classes.headerTitle}>
                    Správa mestskej zelene v Košiciach,<br/>
                    Rastislavova 79, 04001, Košice
                </div>
            </div>

            <div className={classes.title}>Evidenčný list pre Krematórium</div>

            <div className={classes.formRow}>
                <span className={classes.label}>Pohrebná služba (sídlo, IČO):</span>
            </div>
            {renderMultilineField("funeralService")}

            <div className={classes.formRow}>
                <span className={classes.label}>Meno a adresa objednávateľa pohrebu:</span>
            </div>
            {renderMultilineField("orderer")}

            <div className={classes.spacer}/>
            <div className={classes.formRow}>
                <span className={classes.label}>Spopolnenie:</span>
                {renderField("ceremony", classes.shortField)}
            </div>

            <div className={classes.smallSpacer}/>
            <div className={classes.formRow}>
                <span className={classes.label}>Faktúra číslo:</span>
                {renderField("invoiceNumber", classes.shortField)}
            </div>
            <div className={classes.formRow}>
                <span className={classes.label}>Urnu zaslať:</span>
                {renderField("urnSendTo")}
            </div>
        </div>
    );

    const fileNameSuffix = (funeral.deceased || fields.orderer || "evidencny-list").replace(/\s/g, "");

    const showDocument = () => (
        <>
            <div className={classes.editHint}>Po KLIKNUTÍ viete dopisovať alebo meniť údaje.</div>
            <div className="document-preview-frame" style={{border: "1px solid"}}>
                <PDFExport
                    ref={pdfRef}
                    {...getPdfExportProxyProps()}
                    paperSize="A4"
                    scale={0.75}
                    fileName={`evidencny-list-pre-krematorium-${fileNameSuffix}`}
                >
                    <div className={`a4-paper ${classes.document}`}>
                        {renderCopy()}
                        {renderCopy(true)}
                    </div>
                </PDFExport>
            </div>
        </>
    );

    return (
        <Container maxWidth="lg" className={classes.seeMore}>
            <div className={classes.root}>
                <Paper className={fixedHeightPaper}>
                    <Grid container>
                        <Grid xs={12} sm={4} md={3} lg={3} item container>
                            <Toolbar>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    className={classes.button}
                                    onClick={() => setSuccess(true)}
                                >
                                    Späť na detaily
                                </Button>
                            </Toolbar>
                        </Grid>
                        <Grid xs={12} sm={8} md={9} lg={9} item container justifyContent="flex-end">
                            <Toolbar className={`${classes.toolbarButtons} document-actions-toolbar`}>
                                <ActionButton color="primary">
                                    <Tooltip title="Pdf export">
                                        <PictureAsPdfIcon fontSize="small" onClick={handleExport}/>
                                    </Tooltip>
                                </ActionButton>
                                <GeneratedDocumentEmailAction
                                    pdfRef={pdfRef}
                                    funeral={funeral}
                                    user={user}
                                />
                            </Toolbar>
                        </Grid>
                    </Grid>

                    <Divider/>
                    <Grid
                        className={classes.marginTop}
                        container
                        spacing={0}
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {showDocument()}
                    </Grid>
                </Paper>
            </div>
            {success ? <Redirect to={{
                pathname: `/pohreb/${funeralId.funeral_id}/info`,
                state: {from: props.location, number: 5},
            }}/> : <></>}
        </Container>
    );
};

export default CrematoriumEvidenceListGenerator;
